import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'

export const DEFAULT_EVENT_TIMEZONE = 'UTC'

const COUNTRY_TIMEZONE_OVERRIDES = {
  AR: 'America/Argentina/Buenos_Aires',
  BO: 'America/La_Paz',
  BR: 'America/Sao_Paulo',
  CL: 'America/Santiago',
  CO: 'America/Bogota',
  CR: 'America/Costa_Rica',
  DO: 'America/Santo_Domingo',
  EC: 'America/Guayaquil',
  ES: 'Europe/Madrid',
  GT: 'America/Guatemala',
  HN: 'America/Tegucigalpa',
  MX: 'America/Mexico_City',
  NI: 'America/Managua',
  PA: 'America/Panama',
  PE: 'America/Lima',
  PR: 'America/Puerto_Rico',
  PY: 'America/Asuncion',
  SV: 'America/El_Salvador',
  US: 'America/New_York',
  UY: 'America/Montevideo',
  VE: 'America/Caracas',
}

const COMMON_TIMEZONE_OPTIONS = [
  'UTC',
  'America/Guayaquil',
  'America/Bogota',
  'America/Lima',
  'America/Panama',
  'America/Costa_Rica',
  'America/El_Salvador',
  'America/Guatemala',
  'America/Managua',
  'America/Tegucigalpa',
  'America/Mexico_City',
  'America/Cancun',
  'America/Monterrey',
  'America/Tijuana',
  'America/Caracas',
  'America/Santiago',
  'America/La_Paz',
  'America/Sao_Paulo',
  'America/Asuncion',
  'America/Montevideo',
  'America/Argentina/Buenos_Aires',
  'America/Santo_Domingo',
  'America/Puerto_Rico',
  'Europe/Madrid',
  'America/New_York',
]

function hasIntlTimeZoneSupport() {
  return typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function'
}

export function normalizeTimeZone(value, fallback = DEFAULT_EVENT_TIMEZONE) {
  const candidate = value ? String(value).trim() : ''
  if (!candidate || !hasIntlTimeZoneSupport()) {
    return fallback
  }

  try {
    Intl.DateTimeFormat('en-US', { timeZone: candidate }).format(new Date())
    return candidate
  } catch (_error) {
    return fallback
  }
}

function readCountryCode(countryLike) {
  if (!countryLike) return ''
  if (typeof countryLike === 'string') return countryLike.trim().toUpperCase()
  return String(countryLike.code || countryLike.country_code || '').trim().toUpperCase()
}

export function resolveCountryTimeZone(countryLike, fallback = DEFAULT_EVENT_TIMEZONE) {
  const code = readCountryCode(countryLike)
  return normalizeTimeZone(COUNTRY_TIMEZONE_OVERRIDES[code] || '', fallback)
}

export function resolveEventTimeZone(source, fallbackCountry = null) {
  if (typeof source === 'string') {
    return normalizeTimeZone(source)
  }

  if (source && typeof source === 'object') {
    const explicit = normalizeTimeZone(source.timezone || source.default_timezone || '', '')
    if (explicit) {
      return explicit
    }

    const nestedCountry = source.country_detail || source.country || fallbackCountry
    const fromCountry = resolveCountryTimeZone(nestedCountry, '')
    if (fromCountry) {
      return fromCountry
    }
  }

  return resolveCountryTimeZone(fallbackCountry, DEFAULT_EVENT_TIMEZONE)
}

export function getTimeZoneOptions() {
  const supported = typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : []

  return Array.from(new Set([...COMMON_TIMEZONE_OPTIONS, ...supported])).sort((left, right) => left.localeCompare(right))
}

export function formatEventDate(value, source, options = {}, locale = 'es-EC') {
  if (!value) {
    return 'Fecha por confirmar'
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return 'Fecha por confirmar'
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: resolveEventTimeZone(source),
    ...options,
  }).format(parsed)
}

export function formatEventDateRange(startDate, endDate, showEndDate, source, options = { day: '2-digit', month: '2-digit', year: 'numeric' }, locale = 'es-EC') {
  const startLabel = formatEventDate(startDate, source, options, locale)
  if (!showEndDate || !endDate) {
    return startLabel
  }

  return `${startLabel} - ${formatEventDate(endDate, source, options, locale)}`
}

export function buildEventDateEntries(event, options = { day: '2-digit', month: 'long', year: 'numeric' }, locale = 'es-EC') {
  if (!event) {
    return [{ label: 'Fecha', value: 'Fecha por confirmar' }]
  }

  if (event.show_end_date && event.end_date) {
    return [
      { label: 'Fecha de inicio', value: formatEventDate(event.start_date, event, options, locale) },
      { label: 'Fecha de fin', value: formatEventDate(event.end_date, event, options, locale) },
    ]
  }

  return [{ label: 'Fecha', value: formatEventDate(event.start_date, event, options, locale) }]
}

export function toEventDateTimeInput(value, source) {
  if (!value) {
    return ''
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }

  return formatInTimeZone(parsed, resolveEventTimeZone(source), "yyyy-MM-dd'T'HH:mm")
}

export function toUtcIsoFromEventDateTimeInput(value, source) {
  if (!value) {
    return ''
  }

  try {
    return zonedTimeToUtc(value, resolveEventTimeZone(source)).toISOString()
  } catch (_error) {
    return ''
  }
}

export function formatEventInputPreview(value, source, locale = 'es-EC', options = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) {
  const utcValue = toUtcIsoFromEventDateTimeInput(value, source)
  if (!utcValue) {
    return ''
  }

  return formatEventDate(utcValue, source, options, locale)
}
